import { useMutation } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { useState } from "react";

import { useGlobalAnalysisNotificationsContext } from "../components";
import { getAnalysisNotificationStorageKey } from "../components/global-analysis-notifications/utils";
import useQuotaExceededForProduct, {
  PRODUCT_NAMES
} from "../hooks/useQuotaExceededForProduct";
import {
  AnalysisStatus,
  NotificationsLevel,
  SubmitUrlModalDefaultMessages,
  UrlScraper
} from "../lib/enums";
import usePrepareReanalyzeData from "../modules/header/hooks/usePrepareReanalyzeData";
import {
  useNetworkNavigation,
  useUrlDynamicAnalysisStatus
} from "../modules/network/hooks";
import { getIsFinished } from "../modules/network/hooks/useUrlDynamicAnalysisStatus";
import {
  INITIAL_INTERACTIVE_PARAMS,
  INITIAL_RLCS_PARAMS
} from "../modules/samples/components/reanalyze/reanalyze-popup/consts";
import { QUOTA_EXCEEDED_MSG } from "../modules/samples/components/rlcloudsandbox/historytable/HistoryTitle";
import api from "../services/api";
import { route } from "../shim/jsbridge";
import tcbase from "../shim/tcbase";
import ModalWrapper from "./ModalWrapper";
import SubmitUrlForm from "./SubmitUrlForm";
import { prependHttpToUrl, validateUrl } from "../modules/network/util";

const { notifications, consts: { cloud_enabled, t1000_enabled } = {} } =
  tcbase || {};

const isTiCloudDisabled = !(cloud_enabled || t1000_enabled);

const INITIAL_VALUES = {
  url: "",
  fileless: true,
  rl_dynamic_analysis: false,
  rlcs_interactive: false,
  ...INITIAL_RLCS_PARAMS,
  ...Object.entries(INITIAL_INTERACTIVE_PARAMS).reduce((acc, [key, value]) => {
    acc[`interactive_${key}`] = value;
    return acc;
  }, {})
};

export const getScraper = isRLDynamicAnalysis => {
  if (isRLDynamicAnalysis) return UrlScraper.TI_CLOUD;
  return undefined;
};

const handleDirtyFields = (previousValues, currentValues, isDirtyCallback) => {
  Object.keys(previousValues).forEach(formKey => {
    const prev = previousValues?.[formKey];
    const curr = currentValues?.[formKey] ?? prev;
    if (prev !== curr) {
      isDirtyCallback(formKey);
    }
  });
};

const getAdditionalInformation = (
  isRlcsQuotaExceeded,
  isRlcsQuotaExceededLoading,
  additionalInfo
) => {
  if (isRlcsQuotaExceeded) {
    return QUOTA_EXCEEDED_MSG;
  }
  if (isRlcsQuotaExceededLoading) {
    return "Loading...";
  }
  return additionalInfo;
};

const getDisplayInformation = status =>
  AnalysisStatus[status?.toUpperCase() ?? ""];

const SubmitUrlModal = ({
  show,
  handleShow,
  url,
  initialValues = {},
  customMessages = {},
  linksReloadDocument
}) => {
  const [errors, setErrors] = useState({});
  const { navigateToUriPage } = useNetworkNavigation(linksReloadDocument);
  const { addAnalysis: addToUrlAnalysisTracker } =
    useGlobalAnalysisNotificationsContext();

  const {
    isQuotaExceeded: isRlcsQuotaExceeded,
    isLoading: isRlcsQuotaExceededLoading
  } = useQuotaExceededForProduct(PRODUCT_NAMES.TCA_0207_URL_DYNAMIC_ANALYSIS);

  const { data: { dynamic_analysis } = {} } = usePrepareReanalyzeData();
  const automatedRlcs = dynamic_analysis?.rl_dynamic_analysis;
  const interactiveRlcs = dynamic_analysis?.rl_dynamic_analysis_interactive;

  const clearErrors = errKey => {
    // clear specific errKey or clear all if not provided
    if (!errKey) setErrors({});
    else setErrors(({ [errKey]: _, ...rest }) => rest);
  };

  const scraper = getScraper(initialValues?.rl_dynamic_analysis);
  const [values, setValues] = useState({
    ...INITIAL_VALUES,
    ...{
      ...initialValues,
      url: url ?? "",
      ...(scraper ? { scraper } : {})
    }
  });

  const notificationMessages = {
    ...SubmitUrlModalDefaultMessages,
    ...(customMessages || {})
  };

  const handleClose = () => {
    clearErrors();
    handleShow();
  };

  const handleValues = currValues => {
    let newValues = currValues;
    if (!currValues.scraper) {
      newValues = { scraper, ...currValues };
    }
    setValues(prevValues => {
      handleDirtyFields(prevValues, newValues, clearErrors);
      return {
        ...prevValues,
        ...newValues
      };
    });
  };

  const query = useUrlDynamicAnalysisStatus(url);
  const { automated, interactive } = query?.data ?? {};

  const { mutate: handleSubmit, isPending: isLoading } = useMutation({
    mutationFn: () =>
      api.uriTableFetchAndAnalyze(
        prependHttpToUrl(values.url),
        values.scraper,
        values.platform,
        values.geolocation,
        values.locale,
        values.execution_timeout,
        values.interactive_platform,
        values.interactive_execution_timeout,
        values.interactive_geolocation,
        values.interactive_locale,
        values.rl_dynamic_analysis,
        values.rlcs_interactive,
        values.fileless,
        values.joe
      ),
    onSuccess: ({
      data: {
        url_analysis_id: urlAnalysisId,
        url_analysis_sha1: urlAnalysisSha1,
        url_analysis_error_message: urlAnalysisErrorMessage
      }
    }) => {
      handleClose();

      addToUrlAnalysisTracker(values.url);
      const analysisNotificationKey = getAnalysisNotificationStorageKey(
        values.url
      );
      localStorage.removeItem(analysisNotificationKey);

      navigateToUriPage(prependHttpToUrl(values.url));
      if (!values.rlcs_interactive) return;
      if (urlAnalysisSha1 && urlAnalysisId) {
        window.open(
          route("rl_interactive_analysis_for_url", [
            encodeURIComponent(values.url),
            urlAnalysisSha1,
            urlAnalysisId
          ]),
          "_blank"
        );
      } else {
        notifications.show({
          level: NotificationsLevel.ERROR,
          message:
            urlAnalysisErrorMessage ??
            notificationMessages.interactiveAnalysisError
        });
      }
    },
    onError: error => {
      const { message, url: urlErr } = error.data || {};
      const errorMessage = urlErr?.[0] || message || "";
      setErrors(error.data);
      notifications.show({
        level: "error",
        message: errorMessage || notificationMessages.error
      });
    }
  });

  const {
    mutate: validateUploadSample,
    isPending: validateUploadSamplePending
  } = useMutation({
    mutationFn: () =>
      api.validateUploadSample(
        false,
        values.platform,
        values.geolocation,
        values.locale,
        undefined,
        values.execution_timeout,
        undefined,
        values.interactive_platform,
        undefined,
        values.interactive_execution_timeout
      ),
    onSuccess: () => {
      handleSubmit();
    },
    onError: error => {
      const { data: errorData } = error;
      setErrors(errorData);
    }
  });

  return (
    <ModalWrapper
      show={show}
      title="URL Analysis"
      size="small"
      onHide={() => {
        handleClose();
      }}
      data-testid="submit-url-modal"
      preventOtherActions
    >
      <SubmitUrlForm
        values={values}
        errors={errors}
        onSubmit={() => {
          if (isLoading || validateUploadSamplePending || !show) return;
          const validationError = validateUrl(prependHttpToUrl(values.url));
          if (validationError) {
            notifications.show({
              level: NotificationsLevel.ERROR,
              message: validationError,
              aboveModal: true
            });
            return;
          }
          if (values?.rl_dynamic_analysis || values?.rlcs_interactive) {
            validateUploadSample();
            return;
          }
          handleSubmit();
        }}
        setShow={() => {
          handleClose();
        }}
        onValueChange={val =>
          handleValues({
            ...val,
            ...(val?.url ? { url: val?.url?.trim() } : {})
          })
        }
        isUrlDisabled={Boolean(url)}
        isTiCloudDisabled={isTiCloudDisabled}
        isRLDynamicDisabled={
          query.isSuccess && !getIsFinished(automated?.analysis_status)
        }
        isInteractiveDisabled={
          query.isSuccess && !getIsFinished(interactive?.analysis_status)
        }
        isSubmitButtonDisabled={
          isLoading || validateUploadSamplePending || !show
        }
        interactiveAnalysisInfo={{
          enabled:
            interactiveRlcs?.enabled &&
            !isRlcsQuotaExceeded &&
            !isRlcsQuotaExceededLoading,
          display_status: getDisplayInformation(interactive?.analysis_status),
          additional_information: getAdditionalInformation(
            isRlcsQuotaExceeded,
            isRlcsQuotaExceededLoading,
            interactiveRlcs?.additional_information
          )
        }}
        rlcsAnalysisInfo={{
          enabled:
            automatedRlcs?.enabled &&
            !isRlcsQuotaExceeded &&
            !isRlcsQuotaExceededLoading,
          display_status: getDisplayInformation(automated?.analysis_status),
          additional_information: getAdditionalInformation(
            isRlcsQuotaExceeded,
            isRlcsQuotaExceededLoading,
            automatedRlcs?.additional_information
          )
        }}
      />
    </ModalWrapper>
  );
};

SubmitUrlModal.propTypes = {
  url: PropTypes.string,
  show: PropTypes.bool.isRequired,
  handleShow: PropTypes.func.isRequired,
  customMessages: PropTypes.shape({}),
  initialValues: PropTypes.shape({
    rl_dynamic_analysis: PropTypes.bool
  }),
  linksReloadDocument: PropTypes.bool
};

export default SubmitUrlModal;
